import { Box } from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import { ByzMuiDateTimePicker } from "src/components/core-components/mui/inputs/ByzMuiDateTimePicker";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import { useTranslate } from "src/locales/use-locales";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import { useAppSelector } from "src/app/hooks";
import { OtherPrivilegeGuard } from "src/utils/authorization/OtherPrivilegeGuard";
import { useCardForm } from "src/hooks/useCardForm";
import ByzMcParameterDetailSelect from "src/components/shared-components/ByzMcParameterDetailSelect";
import { McParameterDetailResponseDto } from "src/api/references/referenceServicesDtos";
import { useCallback, useEffect } from "react";

interface NeomodScoreFormData {
  NS_ID: string;
  PROCESS_TIME: Date | null;
  NURSE_ID: string;
  CTIME: Date;
  CUSER: string;
  CALC_VALUE: string;
  MORTALITY_RATIO: string;
  CENTRAL_NERVOUS_SYSTEM_ID: string;
  HEMOCOAGULATION_BALANCE_ID: string;
  RESPIRATORY_SYSTEM_ID: string;
  GASTROINTESTINAL_SYSTEM_ID: string;
  CARDIOVASCULAR_SYSTEM_ID: string;
  RENAL_SYSTEM_ID: string;
  ACID_BASE_BALANCE_ID: string;
}

const NEOMOD_PARAMETER_KEYS = [
  "CENTRAL_NERVOUS_SYSTEM_ID",
  "HEMOCOAGULATION_BALANCE_ID",
  "RESPIRATORY_SYSTEM_ID",
  "GASTROINTESTINAL_SYSTEM_ID",
  "CARDIOVASCULAR_SYSTEM_ID",
  "RENAL_SYSTEM_ID",
  "ACID_BASE_BALANCE_ID",
] as const;

interface NeomodScoreProps {
  recordId?: string;
  patientId: number;
  onClose?: () => void;
  getComId: () => Promise<number | null>;
  comId: number;
  setComId: (comId: number) => void;
  getCardTableData: (comId: number) => Promise<string | "">;
  createTableMasterId: () => Promise<string | null>;
  save: (id: string) => Promise<void>;
  handleChange?: (field: keyof NeomodScoreFormData, value: any) => void;
  patientData: any;
  isSaving?: boolean;
  extraAccess?: boolean;
}

function NeomodScore({
  recordId,
  save,
  handleChange: parentHandleChange = () => { },
  onClose,
  isSaving,
  extraAccess,
}: NeomodScoreProps) {
  const { t } = useTranslate();
  const { user } = useAppSelector((state) => state.auth);

  const {
    formData,
    loading,
    handleChange,
    handleMCPChange,
    selectedMCPs,
    cardData,
    isEditMode,
    mcpCacheLoaded,
    getMCPParameterDetails,
    getScoreValue,
  } = useCardForm({
    cardType: "NEOMOD_SCORE",
    recordId,
    user,
    onLoadRecord: (record) => {
      Object.entries(record).forEach(([key, value]) => {
        parentHandleChange(key as keyof NeomodScoreFormData, value);
      });
    },
  });

  const typedFormData = formData as unknown as NeomodScoreFormData;

  const calculateTotalScore = useCallback(() => {
    return NEOMOD_PARAMETER_KEYS.reduce(
      (total, key) => total + getScoreValue(key),
      0
    );
  }, [getScoreValue]);

  useEffect(() => {
    Object.entries(formData).forEach(([key, value]) => {
      parentHandleChange(key as keyof NeomodScoreFormData, value);
    });
  }, [formData, parentHandleChange]);

  useEffect(() => {
    const totalScore = calculateTotalScore();
    const totalScoreText = totalScore || totalScore === 0 ? totalScore.toString() : "";

    if ((typedFormData.CALC_VALUE || "") !== totalScoreText) {
      handleChange("CALC_VALUE", totalScoreText);
      parentHandleChange("CALC_VALUE", totalScoreText);
    }

    let mortalityRatio = "";
    if (totalScore >= 0 && totalScore <= 5) {
      mortalityRatio = "1";
    } else if (totalScore > 5 && totalScore <= 7) {
      mortalityRatio = "29";
    } else if (totalScore >= 8) {
      mortalityRatio = "78";
    }

    if ((typedFormData.MORTALITY_RATIO || "") !== mortalityRatio) {
      handleChange("MORTALITY_RATIO", mortalityRatio);
      parentHandleChange("MORTALITY_RATIO", mortalityRatio);
    }
  }, [
    calculateTotalScore,
    handleChange,
    parentHandleChange,
    selectedMCPs,
    typedFormData.CALC_VALUE,
    typedFormData.MORTALITY_RATIO,
  ]);

  const renderMCPField = (
    fieldName: keyof NeomodScoreFormData,
    labelKey: string,
    mcpName: keyof typeof cardData.mcpConfig,
    readOnly: boolean = false
  ) => {
    const mcpConfig = cardData.mcpConfig[mcpName];
    const mcpId = mcpConfig?.mcpId;

    return (
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid
          xs={12}
          sm={6}
          md={6}
          sx={{ borderBottom: "1px solid #E0E0E0" }}
        >
          <ByzMuiTypography sx={{ marginLeft: "30px" }}>
            {t(labelKey)}
          </ByzMuiTypography>
        </Grid>
        <Grid xs={12} sm={6} md={6}>
          <Box sx={{ width: "100%" }}>
              <ByzMcParameterDetailSelect
                title={t("select")}
                mcpId={mcpId}
                multiple={false}
                handleChange={(val) => {
                  if (!val) {
                    handleMCPChange(mcpName as string, null);
                  } else if (!Array.isArray(val)) {
                    const selected = val as McParameterDetailResponseDto;
                    handleMCPChange(mcpName as string, selected);
                  }
                }}
                value={selectedMCPs?.[mcpName as string] || null}
                sx={{ width: "100%" }}
                cachedData={mcpId ? getMCPParameterDetails(mcpId) : []}
                cacheLoaded={mcpCacheLoaded}
                readOnly={readOnly}
              />
          </Box>
        </Grid>
      </Grid>
    );
  };


  return (
    <>
      <Box sx={{ padding: "20px", width: "100%" }}>
        <Box mb={3}>
          <OtherPrivilegeGuard
            extraAccess={!!extraAccess}
            fallback={
              <ByzMuiSaveButton
                disabled
                tooltip={!extraAccess ? t("no-permission-for-this-action") : undefined}
              />
            }
          >
            <ByzMuiSaveButton
              onClick={() => save(typedFormData.NS_ID || recordId || "")}
              loading={isSaving}
            />
          </OtherPrivilegeGuard>
        </Box>

        <Grid container spacing={2}>
          <Grid
            xs={12}
            sm={6}
            md={6}
            container
            direction="column"
            spacing={3}
          >
            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle>
                {t("neomod.score")}
              </ByzMuiTypography>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("datetime")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMuiDateTimePicker
                  sx={{ width: "100%" }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      label: t("datetime"),
                    },
                  }}
                  value={typedFormData.PROCESS_TIME}
                  onChange={(value) => handleChange("PROCESS_TIME", value)}
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("createdby")}
                </ByzMuiTypography>
              </Grid>
              <Grid xs={12} sm={6} md={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  color="info"
                  label={t("createdby")}
                  variant="outlined"
                  value={typedFormData.CUSER || user?.username || ""}
                  disabled
                />
              </Grid>
            </Grid>

            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle>
                {t("neomod.patient-related-factors")}
              </ByzMuiTypography>
            </Grid>

            {renderMCPField(
              "CENTRAL_NERVOUS_SYSTEM_ID",
              "neomod.virtual-neural-parameters",
              "CENTRAL_NERVOUS_SYSTEM_ID",
              !extraAccess
            )}
            {renderMCPField(
              "HEMOCOAGULATION_BALANCE_ID",
              "neomod.hemocoagulation-balance",
              "HEMOCOAGULATION_BALANCE_ID",
              !extraAccess
            )}
            {renderMCPField(
              "RESPIRATORY_SYSTEM_ID",
              "neomod.respiratory-system",
              "RESPIRATORY_SYSTEM_ID",
              !extraAccess
            )}
            {renderMCPField(
              "GASTROINTESTINAL_SYSTEM_ID",
              "neomod.gastrointestinal-system",
              "GASTROINTESTINAL_SYSTEM_ID",
              !extraAccess
            )}
            {renderMCPField(
              "CARDIOVASCULAR_SYSTEM_ID",
              "neomod.cardiovascular-system",
              "CARDIOVASCULAR_SYSTEM_ID",
              !extraAccess
            )}
            {renderMCPField(
              "RENAL_SYSTEM_ID",
              "neomod.risk-area-skin-type",
              "RENAL_SYSTEM_ID",
              !extraAccess
            )}
            {renderMCPField(
              "ACID_BASE_BALANCE_ID",
              "neomod.acid-base",
              "ACID_BASE_BALANCE_ID",
              !extraAccess
            )}

            <Grid
              md={6}
              sx={{
                backgroundColor: "#F4F7FE",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <ByzMuiTypography isTitle>{t("other")}</ByzMuiTypography>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("neomod.puan")}
                </ByzMuiTypography>
              </Grid>

              <Grid xs={12} sm={6} md={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  color="info"
                  label={t("neomod.puan")}
                  variant="outlined"
                  value={typedFormData.CALC_VALUE || ""}
                  disabled
                />
              </Grid>
            </Grid>

            <Grid container alignItems="center" justifyContent="space-between">
              <Grid
                xs={12}
                sm={6}
                md={6}
                sx={{ borderBottom: "1px solid #E0E0E0" }}
              >
                <ByzMuiTypography sx={{ marginLeft: "30px" }}>
                  {t("neomod.morale-rate")}
                </ByzMuiTypography>
              </Grid>

              <Grid xs={12} sm={6} md={6}>
                <ByzMuiTextField
                  sx={{ width: "100%" }}
                  color="info"
                  label={t("neomod.morale-rate")}
                  variant="outlined"
                  value={typedFormData.MORTALITY_RATIO || ""}
                  disabled
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </>
  );
}

export default NeomodScore;
